import { useState, useEffect, forwardRef } from 'react';
import { VariableSizeList } from 'react-window';
import { isEmpty } from 'lodash-es';

import { getUsers } from '@/api/api';

interface User {
  id: string;
  displayName: string;
  mail?: string;
}

const UsersPicker = ({ placeholder, source, onChange }: { placeholder: string; source: number; onChange: (u: User[]) => void }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<User[]>([]);
  const [selected, setSelected] = useState<User[]>([]);
  const [input, setInput] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);

  /** 
   * Handle fetching users
  **/
  const fetchPage = async () => {
    setLoading(true);
    var items: any = []
    if (!isEmpty(input)) {
      const res = await getUsers(source, input);
      items = await res.json();
      items = items.items;
    }
    setOptions(prev => items);
    setLoading(false);
  }

  /** 
   * Debounce search input (delay rate at which event fires and performs API call to query users)
  **/
  useEffect(() => {
    const h = setTimeout(() => { fetchPage(); }, 300);
    return () => clearTimeout(h);
  }, [input])

  const List = forwardRef<HTMLDivElement>(function Listbox(_, ref) {
    const itemCount = options.length;

    return (
      <VariableSizeList
        className="w-full bg-base-100 border border-secondary rounded-lg scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent scrollbar-w-8"
        style={{ position: 'absolute', top: '100%', left: 0, zIndex: 50 }}
        height={240}
        width="100%"
        itemCount={itemCount}
        itemSize={() => 30}
        ref={ref as any}
      >
        {({ index, style }) => (
          <div
            className={`text-sm px-3 py-1 hover:bg-primary/25 cursor-pointer ${
              index < options.length && selected.some(u => u.id === options[index].id)
                ? 'bg-primary/20'
                : ''
            }`}
            style={style}
            onClick={() => {
              if (index >= options.length) return;
              const u = options[index];
              const next = selected.find(s => s.id === u.id)
                ? selected.filter(s => s.id !== u.id)
                : [...selected, u];
              
              onChange(next);
              setInput('');
            }}
          >
            {options[index].displayName || ''}
          </div>
        )}
      </VariableSizeList>
    );
  });

  return (
    <div className="relative w-full">
      <div className="flex flex-wrap items-center gap-1 pb-1 rounded cursor-text" onClick={() => setOpen(true)}>
        <input
          className="flex-grow input input-sm input-bordered border-secondary bg-neutral focus:outline-none focus-within:outline-none shadow-sm"
          placeholder={placeholder}
          value={input}
          onChange={e => setInput(e.target.value)}
          onFocus={() => setOpen(true)}
        />
      </div>

      {loading ?
        <div className="text-center mt-3"><span className="loading loading-spinner loading-md text-primary"></span></div>
      :
        ((open && options.length > 0) ? <List /> : <div></div>)
      }
    </div>
  );
}

export default UsersPicker;
